import '@material/web/textfield/outlined-text-field.js';
import '@material/web/button/filled-button.js';
import '@material/web/button/outlined-button.js';
import '@material/web/button/text-button.js';

import { api, type Member } from '../api.js';
import { ce, el, clear, type TextFieldEl } from '../dom.js';
import { langSwitcher, t, type MessageKey } from '../i18n.js';

interface PresetResult {
  tagsCreated: string[];
  rewardsCreated: string[];
  policiesCreated: string[];
}

const HOUSEHOLDS: { preset: string; label: MessageKey; blurb: MessageKey }[] = [
  { preset: 'single', label: 'setupHouseholdSingle', blurb: 'setupHouseholdSingleBlurb' },
  { preset: 'couple', label: 'setupHouseholdCouple', blurb: 'setupHouseholdCoupleBlurb' },
  { preset: 'family', label: 'setupHouseholdFamily', blurb: 'setupHouseholdFamilyBlurb' },
  { preset: 'shared', label: 'setupHouseholdShared', blurb: 'setupHouseholdSharedBlurb' },
];

/** Where the wizard hands off to: the app, or straight to Admin (ADR-0024). */
export type SetupDone = (member: Member, dest?: 'tasks' | 'admin') => void;

export function renderSetup(onDone: SetupDone): HTMLElement {
  const root = el('div', {});
  renderCreateStep(root, onDone);
  return root;
}

/** A Material button with a click handler — the wizard's only button shape. */
function mdButton(
  kind: 'filled' | 'outlined' | 'text',
  label: string,
  onClick: () => void,
  attrs: Record<string, string> = {},
): HTMLElement {
  const node = ce(`md-${kind}-button`, { type: 'button', ...attrs }, label);
  node.addEventListener('click', onClick);
  return node;
}

/** The wizard's "← Back" step link. */
function backTo(label: string, onClick: () => void): HTMLElement {
  return mdButton('text', label, onClick);
}

function renderCreateStep(root: HTMLElement, onDone: SetupDone): void {
  const error = el('p', { class: 'error' });
  const familyName = ce('md-outlined-text-field', {
    id: 'setup-family', label: t('setupFamilyLabel'), placeholder: t('setupFamilyPlaceholder'),
  }) as TextFieldEl;
  const adminName = ce('md-outlined-text-field', {
    id: 'setup-admin', label: t('setupAdminLabel'), placeholder: t('setupAdminPlaceholder'),
  }) as TextFieldEl;
  const pin = ce('md-outlined-text-field', {
    id: 'setup-pin', label: t('setupPinLabel'), type: 'password', inputmode: 'numeric',
    'supporting-text': t('setupPinPlaceholder'),
  }) as TextFieldEl;

  const form = el(
    'form',
    {
      onsubmit: (ev: Event) => {
        ev.preventDefault();
        error.textContent = '';
        const body: Record<string, string> = {
          familyName: familyName.value.trim(),
          adminName: adminName.value.trim(),
        };
        if (pin.value) body.pin = pin.value;
        api
          .post<{ member: Member }>('/api/setup', body)
          .then((res) => renderStartChoice(root, res.member, onDone))
          .catch((err: Error) => {
            error.textContent = err.message;
          });
      },
    },
    el('div', { class: 'admin-form-fields' }, familyName, adminName, pin),
    error,
    ce('md-filled-button', { type: 'submit' }, t('setupCreate')),
  );

  clear(root);
  root.append(
    el('p', { class: 'wordmark' }, 'FamilyTask'),
    el('div', { class: 'spread' }, el('h1', {}, t('setupWelcome')), langSwitcher()),
    el('p', { class: 'muted' }, t('setupIntro')),
    el('div', { class: 'card' }, form),
  );
}

// Wizard step 2 (ADR-0017): blank start vs guided presets.
function renderStartChoice(root: HTMLElement, member: Member, onDone: SetupDone): void {
  clear(root);
  root.append(
    el('h1', {}, t('setupHowStart')),
    el(
      'div',
      { class: 'card' },
      el(
        'div',
        {
          class: 'profile',
          role: 'button',
          tabindex: '0',
          id: 'start-blank',
          onclick: () => renderAddMembersStep(root, member, onDone),
        },
        el('div', {}, el('strong', {}, t('setupBlankTitle'))),
        el('p', { class: 'muted' }, t('setupBlankBlurb')),
      ),
    ),
    el(
      'div',
      { class: 'card' },
      el(
        'div',
        {
          class: 'profile',
          role: 'button',
          tabindex: '0',
          id: 'start-guided',
          onclick: () => renderHouseholdStep(root, member, onDone),
        },
        el('div', {}, el('strong', {}, t('setupGuidedTitle'))),
        el('p', { class: 'muted' }, t('setupGuidedBlurb')),
      ),
    ),
  );
}

// Wizard step 3: household type. Picking one starts the per-type question
// steps (one question per page, with a step counter).
function renderHouseholdStep(root: HTMLElement, member: Member, onDone: SetupDone): void {
  clear(root);
  root.append(
    el('h1', {}, t('setupHousehold')),
    el(
      'div',
      { class: 'picker' },
      ...HOUSEHOLDS.map((h) =>
        el(
          'div',
          {
            class: 'profile',
            role: 'button',
            tabindex: '0',
            'data-preset': h.preset,
            onclick: () => renderQuestionStep(root, member, h.preset, {}, 0, onDone),
          },
          el('div', {}, el('strong', {}, t(h.label))),
          el('p', { class: 'muted', style: 'text-align:center;font-size:0.85rem' }, t(h.blurb)),
        ),
      ),
    ),
    backTo(t('setupBackArrow'), () => renderStartChoice(root, member, onDone)),
  );
}

// The wizard suggests each piece and asks — it never just creates things.
type QuestionKey = 'job' | 'school' | 'homework' | 'kidApproval';

interface WizardQuestion {
  key: QuestionKey;
  title: MessageKey;
  body: MessageKey;
  yes: MessageKey;
  no: MessageKey;
}

const QUESTIONS: Record<QuestionKey, WizardQuestion> = {
  job: {
    key: 'job',
    title: 'setupQJobTitle',
    body: 'setupQJobBody',
    yes: 'setupQJobYes',
    no: 'setupQJobNo',
  },
  school: {
    key: 'school',
    title: 'setupQSchoolTitle',
    body: 'setupQSchoolBody',
    yes: 'setupQSchoolYes',
    no: 'setupQSchoolNo',
  },
  homework: {
    key: 'homework',
    title: 'setupQHomeworkTitle',
    body: 'setupQHomeworkBody',
    yes: 'setupQHomeworkYes',
    no: 'setupQHomeworkNo',
  },
  kidApproval: {
    key: 'kidApproval',
    title: 'setupQKidApprovalTitle',
    body: 'setupQKidApprovalBody',
    yes: 'setupQKidApprovalYes',
    no: 'setupQKidApprovalNo',
  },
};

function questionsFor(preset: string): WizardQuestion[] {
  return preset === 'family'
    ? [QUESTIONS.job, QUESTIONS.school, QUESTIONS.homework, QUESTIONS.kidApproval]
    : [QUESTIONS.job];
}

function stepCounter(current: number, total: number): HTMLElement {
  return el('p', { class: 'muted step-counter', id: 'step-counter' }, t('setupStep', { current, total }));
}

// Wizard step 4..n: one question per page.
function renderQuestionStep(
  root: HTMLElement,
  member: Member,
  preset: string,
  answers: Partial<Record<QuestionKey, boolean>>,
  index: number,
  onDone: SetupDone,
): void {
  const questions = questionsFor(preset);
  if (index >= questions.length) {
    renderReviewStep(root, member, preset, answers, onDone);
    return;
  }
  const q = questions[index] as WizardQuestion;
  const total = questions.length + 1; // + review step

  function answer(value: boolean): void {
    renderQuestionStep(root, member, preset, { ...answers, [q.key]: value }, index + 1, onDone);
  }

  clear(root);
  root.append(
    stepCounter(index + 1, total),
    el('h1', {}, t(q.title)),
    el('div', { class: 'card' }, el('p', {}, t(q.body))),
    el(
      'div',
      { class: 'row' },
      mdButton('filled', t(q.yes), () => answer(true), { id: 'answer-yes' }),
      mdButton('outlined', t(q.no), () => answer(false), { id: 'answer-no' }),
    ),
    el(
      'button',
      {
        class: 'ghost',
        onclick: () =>
          index === 0
            ? renderHouseholdStep(root, member, onDone)
            : renderQuestionStep(root, member, preset, answers, index - 1, onDone),
      },
      t('setupBackArrow'),
    ),
  );
}

// Wizard final step: review what would be created, then confirm.
function renderReviewStep(
  root: HTMLElement,
  member: Member,
  preset: string,
  answers: Partial<Record<QuestionKey, boolean>>,
  onDone: SetupDone,
): void {
  const questions = questionsFor(preset);
  const total = questions.length + 1;
  const error = el('p', { class: 'error' });
  const planList = el('ul', { id: 'plan-list' });
  const body = { preset, ...answers };

  api
    .post<{ plan: { tags: { name: string }[]; rewards: { title: string; costMinutes: number }[]; policies: string[] } }>(
      '/api/setup/preset-plan',
      body,
    )
    .then(({ plan }) => {
      const items: string[] = [
        ...plan.tags.map((tag) => t('setupPlanLabel', { name: tag.name })),
        ...plan.rewards.map((r) => t('setupPlanReward', { title: r.title, cost: r.costMinutes })),
        ...plan.policies.map((p) => t('setupPlanRule', { rule: p })),
      ];
      if (items.length === 0) {
        planList.append(el('li', {}, t('setupReviewNothing')));
      }
      for (const item of items) planList.append(el('li', {}, item));
    })
    .catch((err: Error) => {
      error.textContent = err.message;
    });

  clear(root);
  root.append(
    stepCounter(total, total),
    el('h1', {}, t('setupReview')),
    el(
      'div',
      { class: 'card' },
      el('p', {}, t('setupReviewIntro')),
      planList,
      el('p', { class: 'muted' }, t('setupReviewEditable')),
    ),
    error,
    el(
      'div',
      { class: 'row' },
      el(
        'button',
        {
          class: 'primary',
          id: 'apply-plan',
          onclick: () => {
            error.textContent = '';
            api
              .post<{ result: PresetResult }>('/api/setup/preset', body)
              .then(({ result }) => renderSummary(root, member, result, onDone))
              .catch((err: Error) => {
                error.textContent = err.message;
              });
          },
        },
        t('setupCreateThese'),
      ),
      el(
        'button',
        {
          class: 'ghost',
          onclick: () => renderQuestionStep(root, member, preset, answers, questions.length - 1, onDone),
        },
        t('setupBackArrow'),
      ),
    ),
  );
}

function renderSummary(
  root: HTMLElement,
  member: Member,
  result: PresetResult,
  onDone: SetupDone,
): void {
  const items: string[] = [
    ...result.tagsCreated.map((name) => t('setupSummaryTag', { name })),
    ...result.rewardsCreated.map((name) => t('setupSummaryReward', { name })),
    ...result.policiesCreated.map((p) => t('setupPlanRule', { rule: p })),
  ];
  clear(root);
  root.append(
    el('h1', {}, t('setupAllSet')),
    el(
      'div',
      { class: 'card', id: 'preset-summary' },
      el('p', {}, items.length > 0 ? t('setupCreatedForYou') : t('setupNothingCreated')),
      el('ul', {}, ...items.map((i) => el('li', {}, i))),
    ),
    mdButton('filled', t('setupNext'), () => renderAddMembersStep(root, member, onDone), { id: 'wizard-done' }),
  );
}

// Wizard final step (ADR-0024): offer to add the rest of the family right
// here — otherwise you land in an app with only yourself. The form is the
// in-wizard path; the ghost button hands off to Admin instead.
function renderAddMembersStep(root: HTMLElement, member: Member, onDone: SetupDone): void {
  const error = el('p', { class: 'error' });
  const added = el('ul', { id: 'added-members' });
  const name = ce('md-outlined-text-field', {
    id: 'wizard-member-name', label: t('setupMemberNameLabel'), placeholder: t('setupMemberPlaceholder'),
  }) as TextFieldEl;

  const form = el(
    'form',
    {
      onsubmit: (ev: Event) => {
        ev.preventDefault();
        error.textContent = '';
        const value = name.value.trim();
        if (!value) return;
        api
          .post<{ member: Member }>('/api/members', { name: value })
          .then((res) => {
            added.append(el('li', {}, res.member.name));
            name.value = '';
            name.focus();
          })
          .catch((err: Error) => {
            error.textContent = err.message;
          });
      },
    },
    el('div', { class: 'admin-form-fields' }, name),
    el('div', { class: 'row' }, ce('md-filled-button', { type: 'submit', id: 'wizard-member-add' }, t('setupAddMemberBtn'))),
  );

  clear(root);
  root.append(
    el('h1', {}, t('setupAddMembersTitle')),
    el(
      'div',
      { class: 'card' },
      el('p', { class: 'muted' }, t('setupAddMembersBlurb')),
      form,
      added,
      error,
    ),
    el(
      'div',
      { class: 'row' },
      mdButton('filled', t('setupMembersDone'), () => onDone(member), { id: 'wizard-members-done' }),
      mdButton('text', t('setupMembersAdmin'), () => onDone(member, 'admin'), { id: 'wizard-members-admin' }),
    ),
  );
}
